import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import PlayerSkillRating from "@/components/training/PlayerSkillRating";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const ACTUAL_STATUSES = [
  { value: "present", labelKey: "training.statusPresent", shortKey: "training.statusPresentShort", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "no_show", labelKey: "training.statusNoShow", shortKey: "training.statusNoShowShort", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "late", labelKey: "training.statusLate", shortKey: "training.statusLateShort", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
];

const RSVP_LABEL_KEYS: Record<string, string> = {
  attending: "training.rsvpAttending",
  not_attending: "training.rsvpNotAttending",
  pending: "training.rsvpPending",
};

interface Props {
  sessionId: string;
  onBack: () => void;
}

export default function SessionAttendance({ sessionId, onBack }: Props) {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [coachNote, setCoachNote] = useState("");
  const [feedbackMap, setFeedbackMap] = useState<Record<string, { comment: string; ratings: Record<string, number> }>>({});
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

  const { data: session } = useQuery({
    queryKey: ["session-detail-att", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name, color)")
        .eq("id", sessionId)
        .single();
      if (data) setCoachNote((data as Record<string, unknown>).coach_note as string || "");
      return data;
    },
  });

  const { data: attendance = [], isLoading } = useQuery({
    queryKey: ["session-attendance", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select("*, profiles!session_attendance_player_id_fkey(full_name)")
        .eq("session_id", sessionId)
        .order("created_at");
      return data ?? [];
    },
  });

  // Load existing feedback for this session
  const { data: existingFeedback = [] } = useQuery({
    queryKey: ["session-feedback", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_feedback")
        .select("player_id, comment, skill_ratings")
        .eq("session_id", sessionId);
      return data ?? [];
    },
  });

  useEffect(() => {
    if (attendance.length > 0 && Object.keys(statuses).length === 0) {
      const map: Record<string, string> = {};
      attendance.forEach((a) => {
        if (a.rsvp_status === "not_attending") {
          map[a.id] = "absent";
        } else {
          map[a.id] = a.actual_status || "present";
        }
      });
      setStatuses(map);
    }
  }, [attendance]);

  // Initialize feedback map from existing data
  useEffect(() => {
    if (existingFeedback.length > 0 && Object.keys(feedbackMap).length === 0) {
      const map: Record<string, { comment: string; ratings: Record<string, number> }> = {};
      existingFeedback.forEach((f) => {
        map[f.player_id] = {
          comment: f.comment || "",
          ratings: (f.skill_ratings as Record<string, number>) || {},
        };
      });
      setFeedbackMap(map);
    }
  }, [existingFeedback]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Save attendance statuses
      const updates = Object.entries(statuses).map(([id, actual_status]) => ({ id, actual_status }));
      for (const u of updates) {
        const { error } = await supabase
          .from("session_attendance")
          .update({ actual_status: u.actual_status })
          .eq("id", u.id);
        if (error) throw error;
      }
      // Save coach note
      const { error: noteError } = await supabase
        .from("training_sessions")
        .update({ coach_note: coachNote || null } as Record<string, unknown>)
        .eq("id", sessionId);
      if (noteError) throw noteError;

      // Save individual feedback
      for (const [playerId, fb] of Object.entries(feedbackMap)) {
        if (!fb.comment && Object.keys(fb.ratings).length === 0) continue;
        const { error } = await supabase.from("player_feedback").upsert(
          {
            session_id: sessionId,
            player_id: playerId,
            coach_id: user!.id,
            comment: fb.comment || null,
            skill_ratings: Object.keys(fb.ratings).length > 0 ? fb.ratings : null,
          } as Record<string, unknown>,
          { onConflict: "session_id,player_id" }
        );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-attendance", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["session-feedback", sessionId] });
      toast({ title: t("training.attendanceSaved") });
    },
    onError: () => toast({ title: t("training.saveFailed"), variant: "destructive" }),
  });

  const toggleStatus = (id: string, value: string) => {
    setStatuses((prev) => ({ ...prev, [id]: value }));
  };

  const updateFeedback = (playerId: string, field: "comment" | "ratings", value: string | Record<string, number>) => {
    setFeedbackMap((prev) => ({
      ...prev,
      [playerId]: {
        comment: prev[playerId]?.comment || "",
        ratings: prev[playerId]?.ratings || {},
        [field]: value,
      },
    }));
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t("training.backToSessionList")}
      </Button>

      {session && (
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              {(session.training_groups as Record<string, unknown> | null)?.color && (
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: (session.training_groups as Record<string, unknown> | null)?.color as string | undefined }} />
              )}
              <span className="font-medium">{(session.training_groups as Record<string, unknown> | null)?.name}</span>
              <span>{format(new Date(session.session_date), "EEE d MMM yyyy", { locale: sv })}</span>
              <span>{session.start_time?.slice(0, 5)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading && <p className="text-muted-foreground text-sm">{t("training.loadingAttendance")}</p>}

      {!isLoading && attendance.length === 0 && (
        <p className="text-muted-foreground text-sm">{t("training.noPlayersRegistered")}</p>
      )}

      <div className="space-y-2">
        {attendance.map((a) => {
          const isCancelled = a.rsvp_status === "not_attending";
          const currentStatus = statuses[a.id] || "present";
          const isExpanded = expandedPlayer === a.player_id;
          const playerFb = feedbackMap[a.player_id] || { comment: "", ratings: {} };

          return (
            <Card key={a.id} className={isCancelled ? "opacity-60" : ""}>
              <CardContent className="py-2.5 px-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{a.profiles?.full_name || t("training.unknownPlayer")}</span>
                    <Badge variant="outline" className="text-xs">
                      {RSVP_LABEL_KEYS[a.rsvp_status] ? t(RSVP_LABEL_KEYS[a.rsvp_status]) : a.rsvp_status}
                    </Badge>
                  </div>
                </div>
                {isCancelled ? (
                  <Badge variant="secondary" className="text-xs">{t("training.absent")}</Badge>
                ) : (
                  <>
                    <div className="flex gap-1.5 flex-wrap">
                      {ACTUAL_STATUSES.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => toggleStatus(a.id, s.value)}
                          className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-all",
                            currentStatus === s.value
                              ? s.color
                              : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted"
                          )}
                        >
                          {t(s.shortKey)}
                        </button>
                      ))}
                    </div>

                    {/* Expandable feedback section */}
                    <Collapsible open={isExpanded} onOpenChange={() => setExpandedPlayer(isExpanded ? null : a.player_id)}>
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1">
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {t("training.individualFeedback")}
                          {playerFb.comment && " ✓"}
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-3 mt-2">
                        <Textarea
                          value={playerFb.comment}
                          onChange={(e) => updateFeedback(a.player_id, "comment", e.target.value)}
                          placeholder={t("training.feedbackPlaceholder")}
                          className="min-h-[50px] text-sm"
                        />
                        <PlayerSkillRating
                          ratings={playerFb.ratings}
                          onChange={(r) => updateFeedback(a.player_id, "ratings", r)}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Coach note */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">{t("training.coachNote")}</label>
        <Textarea
          value={coachNote}
          onChange={(e) => setCoachNote(e.target.value)}
          placeholder={t("training.coachNotePlaceholder")}
          className="min-h-[60px]"
        />
      </div>

      {attendance.length > 0 && (
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-1" /> {t("training.saveAttendance")}
        </Button>
      )}
    </div>
  );
}
